import React, {useMemo, memo} from 'react';
import PropTypes from 'prop-types';
import cl from 'classnames';
import _ from 'lodash';
import {CheckIcon, CloseIcon} from '../SvgComponents/SvgComponents';
import UtilsHelper from '../../utils/utils-helper';
import {t} from '../../services/i18nHandler';

function TariffTableItem({
  tariff, detailsClick, language, activePeriod, detailsDemoClick, servicesKeyOptionsByRole,
}) {
  const name = useMemo(() => (tariff.name || {}), [tariff]);

  const basePrice = useMemo(() => (tariff.basePrice || 0), [tariff]);

  const currentPeriod = useMemo(() => {
    const periods = tariff.pricePeriods || [];
    return _.find(periods, {months: activePeriod}) || {};
  }, [tariff, activePeriod]);

  const priceDimension = useMemo(() => {
    const pd = tariff.priceDimension;
    if (pd === 1) {
      return t('units.rubles');
    }
    if (pd === 2) {
      return t('units.rublesMonth');
    }
    return null;
  }, [tariff, language]);

  const amount = useMemo(() => {
    const amountValue = currentPeriod.amount || tariff.basePrice || 0;
    return UtilsHelper.priceFormat(amountValue);
  }, [currentPeriod, tariff, activePeriod]);

  const discountText = useMemo(() => {
    const {discount, prefix} = currentPeriod;
    if (discount) {
      return (
        <>
          {t('tariffs.discount')}
          {prefix === 1 && (
            <>
              &nbsp;
              {t('tariffs.moreThan')}
            </>
          )}
          &nbsp;
          {discount}
          %
        </>
      );
    }
  }, [tariff, language, currentPeriod]);

  const totalDiscount = useMemo(() => {
    const currentAmount = currentPeriod.amount;
    if (!currentAmount || currentAmount === basePrice) {
      return null;
    }
    if (basePrice) {
      const totalDiscountValue = basePrice - currentAmount;
      return UtilsHelper.priceFormat(totalDiscountValue);
    }
    return null;
  }, [basePrice, currentPeriod]);

  const recordTime = useMemo(() => {
    const options = tariff.standardOptions || {};
    if (options.unlimitedRecord) {
      return (<div className="tariffs_table-view_item_service_infinite" />);
    }
    if (options.totalHoursRecord) {
      return (
        <>
          <strong>{options.totalHoursRecord}</strong>
          &nbsp;
          {t('units.hours')}
        </>
      );
    }
    return null;
  }, [tariff]);

  const attributes = useMemo(() => (tariff.attributes || []), [tariff]);

  return (
    <div className={cl('tariffs_table-view_item', {
      demo: tariff.giveAtRegister,
    })}
    >
      {tariff.giveAtRegister && (
        <div
          className="tariffs_table-view_item_demo"
          role="button"
          onClick={detailsDemoClick}
        >
          {t('tariffs.freeDemoAccess')}
          <i className="tariffs_item_demo_info" />
        </div>
      )}
      <div className="tariffs_table-view_item_name">
        {name[language]}
      </div>
      <div className="tariffs_table-view_item_price">
        <div className="tariffs_table-view_item_price_value">
          {amount}
        </div>
        <div className="tariffs_table-view_item_price_unit">
          {priceDimension}
        </div>
      </div>
      <div className="tariffs_table-view_item_discount_value">
        {discountText}
      </div>
      <div className="tariffs_table-view_item_discount">
        {totalDiscount && (
          <div className="tariffs_table-view_item_discount_total">
            {t('tariffs.totalDiscount')}
            <br />
            <strong>
              {totalDiscount}
              &nbsp;
              {priceDimension}
            </strong>
          </div>
        )}
      </div>
      <div className="tariffs_table-view_item_services">
        {servicesKeyOptionsByRole.map((key) => {
          const isIncludeOption = attributes.includes(key.value);
          if (isIncludeOption) {
            return (
              <div className="tariffs_table-view_item_service" key={key.value}>
                <div className="tariffs_table-view_item_service_active">
                  <CheckIcon />
                </div>
              </div>
            );
          }
          return (
            <div className="tariffs_table-view_item_service" key={key.value}>
              <div className="tariffs_table-view_item_service_disabled">
                <CloseIcon />
              </div>
            </div>
          );
        })}
        <div className="tariffs_table-view_item_service">
          <div className="tariffs_table-view_item_service_hours">
            {recordTime}
          </div>
        </div>
      </div>
      <div className="tariffs_table-view_item_details">
        <div
          role="button"
          className="tariffs_table-view_item_details_btn"
          onClick={detailsClick}
        >
          {t('tariffs.aboutRates')}
        </div>
      </div>
    </div>
  );
}

TariffTableItem.defaultProps = {
  servicesKeyOptionsByRole: [],
  tariff: {},
  detailsClick: () => {},
  language: 'ru',
  activePeriod: 1,
  detailsDemoClick: () => {},
};

TariffTableItem.propTypes = {
  servicesKeyOptionsByRole: PropTypes.arrayOf(PropTypes.shape({})),
  tariff: PropTypes.shape({}),
  detailsClick: PropTypes.func,
  language: PropTypes.string,
  activePeriod: PropTypes.number,
  detailsDemoClick: PropTypes.func,
};

export default memo(TariffTableItem);
